import { createSelector } from 'redux-orm';

import orm from '../orm';
import { selectPath } from './router';
import { selectCurrentUserId } from './users';
import { isLocalId } from '../utils/local-id';
import computeCardPoints, { getLatestArchivedRef } from '../utils/compute-card-points';

export const makeSelectCardById = () =>
  createSelector(
    orm,
    (_, id) => id,
    ({ Card }, id) => {
      const cardModel = Card.withId(id);

      if (!cardModel) {
        return cardModel;
      }

      return {
        ...cardModel.ref,
        coverUrl: cardModel.coverAttachment && cardModel.coverAttachment.coverUrl,
        isPersisted: !isLocalId(id),
      };
    },
  );

export const selectCardById = makeSelectCardById();

export const makeSelectUsersByCardId = () =>
  createSelector(
    orm,
    (_, id) => id,
    ({ Card }, id) => {
      const cardModel = Card.withId(id);

      if (!cardModel) {
        return cardModel;
      }
      return cardModel.users.toRefArray();
    },
  );

export const selectUsersByCardId = makeSelectUsersByCardId();

export const makeSelectLabelsByCardId = () =>
  createSelector(
    orm,
    (_, id) => id,
    ({ Card }, id) => {
      const cardModel = Card.withId(id);

      if (!cardModel) {
        return cardModel;
      }

      return cardModel.labels.toRefArray();
    },
  );

export const selectLabelsByCardId = makeSelectLabelsByCardId();






export const makeSelectTasksByCardId = () =>
  createSelector(
    orm,
    (_, id) => id,
    ({ Card }, id) => {
      const cardModel = Card.withId(id);

      if (!cardModel) {
        return cardModel;
      }

      return cardModel.getOrderedTasksQuerySet().toRefArray();
    },
  );





export const selectTasksByCardId = makeSelectTasksByCardId();

export const makeSelectLastActivityIdByCardId = () =>
  createSelector(
    orm,
    (_, id) => id,
    ({ Card }, id) => {
      const cardModel = Card.withId(id);

      if (!cardModel) {
        return cardModel;
      }

      const lastActivityModel = cardModel.getFilteredOrderedInCardActivitiesQuerySet().last();

      return lastActivityModel && lastActivityModel.id;
    },
  );

export const selectLastActivityIdByCardId = makeSelectLastActivityIdByCardId();

export const makeSelectNotificationsByCardId = () =>
  createSelector(
    orm,
    (_, id) => id,
    ({ Card }, id) => {
      const cardModel = Card.withId(id);

      if (!cardModel) {
        return cardModel;
      }

      return cardModel.getUnreadNotificationsQuerySet().toRefArray();
    },
  );

export const selectNotificationsByCardId = makeSelectNotificationsByCardId();

export const makeSelectNotificationsTotalByCardId = () =>
  createSelector(
    orm,
    (_, id) => id,
    ({ Card }, id) => {
      const cardModel = Card.withId(id);

      if (!cardModel) {
        return cardModel;
      }

      return cardModel.getUnreadNotificationsQuerySet().count();
    },
  );

export const selectNotificationsTotalByCardId = makeSelectNotificationsTotalByCardId();

export const selectCurrentCard = createSelector(
  orm,
  (state) => selectPath(state).cardId,
  ({ Card }, id) => {
    if (!id) {
      return id;
    }

    const cardModel = Card.withId(id);
    if (!cardModel) {
      return cardModel;
    }

    return cardModel.ref;
  },
);

export const selectUsersForCurrentCard = createSelector(
  orm,
  (state) => selectPath(state).cardId,
  ({ Card }, id) => {
    if (!id) {
      return id;
    }

    const cardModel = Card.withId(id);

    if (!cardModel) {
      return cardModel;
    }

    return cardModel.users.toRefArray();
  },
);

export const selectLabelsForCurrentCard = createSelector(
  orm,
  (state) => selectPath(state).cardId,
  ({ Card }, id) => {
    if (!id) {
      return id;
    }

    const cardModel = Card.withId(id);

    if (!cardModel) {
      return cardModel;
    }

    return cardModel.labels.toRefArray();
  },
);

export const selectTasksForCurrentCard = createSelector(
  orm,
  (state) => selectPath(state).cardId,
  ({ Card }, id) => {
    if (!id) {
      return id;
    }

    const cardModel = Card.withId(id);

    if (!cardModel) {
      return cardModel;
    }

    return cardModel
      .getOrderedTasksQuerySet()
      .toRefArray()
      .map((task) => ({
        ...task,
        isPersisted: !isLocalId(task.id),
      }));
  },
);

export const selectAttachmentsForCurrentCard = createSelector(
  orm,
  (state) => selectPath(state).cardId,
  ({ Card }, id) => {
    if (!id) {
      return id;
    }

    const cardModel = Card.withId(id);

    if (!cardModel) {
      return cardModel;
    }

    return cardModel
      .getOrderedAttachmentsQuerySet()
      .toRefArray()
      .map((attachment) => ({
        ...attachment,
        isCover: attachment.id === cardModel.coverAttachmentId,
        isPersisted: !isLocalId(attachment.id),
      }));
  },
);

export const selectActivitiesForCurrentCard = createSelector(
  orm,
  (state) => selectPath(state).cardId,
  (state) => selectCurrentUserId(state),
  ({ Card }, id, currentUserId) => {
    if (!id) {
      return id;
    }

    const cardModel = Card.withId(id);

    if (!cardModel) {
      return cardModel;
    }

    return cardModel
      .getFilteredOrderedInCardActivitiesQuerySet()
      .toModelArray()
      .map((activityModel) => ({
        ...activityModel.ref,
        isPersisted: !isLocalId(activityModel.id),
        user: {
          ...activityModel.user.ref,
          isCurrent: activityModel.user.id === currentUserId,
        },
      }));
  },
);

export const selectNotificationIdsForCurrentCard = createSelector(
  orm,
  (state) => selectPath(state).cardId,
  ({ Card }, id) => {
    if (!id) {
      return id;
    }

    const cardModel = Card.withId(id);

    if (!cardModel) {
      return cardModel;
    }

    return cardModel
      .getUnreadNotificationsQuerySet()
      .toRefArray()
      .map((notification) => notification.id);
  },
);

export const makeSelectPointsDataByCardId = () =>
  createSelector(
    orm,
    (_, cardId) => cardId,
    (_, __, pointSystemId) => pointSystemId,
    (
      {
        Card,
        CardPointData,
        CardPointAdvancedData,
        CardPointArchivedData,
        ContentCardDefault,
        ContentViewPlan,
        DampingCoefficient,
        ListReportData,
      },
      cardId,
      pointSystemId,
    ) => {
      if (!cardId || !pointSystemId) {
        return null;
      }

      const cardModel = Card.withId(cardId);
      if (!cardModel) {
        return null;
      }
      const listId = cardModel.listId;

      const listReportDataModel = listId
        ? ListReportData.all().filter({ listId }).first()
        : null;

      const reportYear = listReportDataModel?.year ? Number(listReportDataModel.year) : null;
      const reportMonth = listReportDataModel?.month ? Number(listReportDataModel.month) : null;

      // Без периода списка бейдж не показываем
      if (!reportYear || !reportMonth) {
        return null;
      }

      const dampingCoefficientModels = DampingCoefficient.all()
        .filter({ pointSistemId: pointSystemId })
        .toModelArray();

      const dampingCoefficientsHash =
        dampingCoefficientModels.length > 0
          ? `${dampingCoefficientModels.length}:${dampingCoefficientModels
              .map((dc) => `${dc.id}:${dc.from}:${dc.to}:${dc.value}`)
              .sort()
              .join('|')}`
          : '0';

      const cardPointDataModel = CardPointData.all()
        .filter({ cardId, pointSistemId: pointSystemId })
        .first();

      const cardPointAdvancedDataModels = CardPointAdvancedData.all()
        .filter({ cardId, pointSistemId: pointSystemId })
        .toRefArray();

      if (!cardPointDataModel && cardPointAdvancedDataModels.length === 0) {
        return null;
      }

      const cardPointData = cardPointDataModel?.ref || null;
      const cardPointAdvancedData = cardPointAdvancedDataModels || [];

      let contentCardDefault = null;
      if (cardPointDataModel?.ccdId) {
        const contentCardDefaultModel = ContentCardDefault.withId(cardPointDataModel.ccdId);
        if (
          contentCardDefaultModel &&
          contentCardDefaultModel.pointSistemId === pointSystemId
        ) {
          contentCardDefault = contentCardDefaultModel.ref;
        }
      }

      const archivedModels = cardPointDataModel
        ? CardPointArchivedData.all()
            .filter({ cpdId: cardPointDataModel.id, pointSistemId: pointSystemId })
            .toModelArray()
        : [];
      const archivedData = getLatestArchivedRef(archivedModels);

      const allContentViewPlans = ContentViewPlan.all()
        .filter({ pointSistemId: pointSystemId })
        .toModelArray();

      const contentViewPlanModel =
        allContentViewPlans.find((plan) => {
          const planYear = typeof plan.year === 'number' ? plan.year : Number(plan.year);
          const planMonth = typeof plan.month === 'number' ? plan.month : Number(plan.month);
          return (
            !Number.isNaN(planYear) &&
            !Number.isNaN(planMonth) &&
            planYear === reportYear &&
            planMonth === reportMonth
          );
        }) || null;

      const contentViewPlan = contentViewPlanModel?.ref || null;
      const contentViewPlanHash = contentViewPlanModel
        ? `${contentViewPlanModel.id}:${contentViewPlanModel.year}:${contentViewPlanModel.month}:${contentViewPlanModel.photo}:${contentViewPlanModel.video}:${contentViewPlanModel.caseCount}:${JSON.stringify(
            contentViewPlanModel.values || {},
          )}`
        : '0';

      const dampingCoefficients = dampingCoefficientModels
        .sort((a, b) => (a.id || 0) - (b.id || 0))
        .map((model) => model.ref);

      const points = computeCardPoints({
        cardPointData,
        contentCardDefault,
        cardPointAdvancedData,
        archivedData,
        contentViewPlan,
        dampingCoefficients,
      });

      return {
        contentCardDefault: points.contentCardDefault,
        cardPointAdvancedData: points.cardPointAdvancedData,
        totalPoints: points.totalPoints,
        coefficient: points.coefficient,
        basePoints: points.basePoints,
        extraPoints: points.extraPoints,
        fromArchive: points.fromArchive,
        _dampingCoefficientsHash: dampingCoefficientsHash,
        _contentViewPlanHash: contentViewPlanHash,
      };
    },
  );

export const selectPointsDataByCardId = makeSelectPointsDataByCardId();

export default {
  makeSelectCardById,
  selectCardById,
  makeSelectUsersByCardId,
  selectUsersByCardId,
  makeSelectLabelsByCardId,
  selectLabelsByCardId,
  makeSelectTasksByCardId,
  selectTasksByCardId,
  makeSelectLastActivityIdByCardId,
  selectLastActivityIdByCardId,
  makeSelectNotificationsByCardId,
  selectNotificationsByCardId,
  makeSelectNotificationsTotalByCardId,
  selectNotificationsTotalByCardId,
  selectCurrentCard,
  selectUsersForCurrentCard,
  selectLabelsForCurrentCard,
  selectTasksForCurrentCard,
  selectAttachmentsForCurrentCard,
  selectActivitiesForCurrentCard,
  selectNotificationIdsForCurrentCard,
  makeSelectPointsDataByCardId,
  selectPointsDataByCardId,
};
